import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import featureGatingService from '../services/featureGatingService';

/**
 * Reactive feature-flag hook.
 * Returns { flags, isEnabled, loading } and updates live when admin changes
 * either userOverrides/{uid} or adminConfig/subscriptionPlans.
 *
 * Defaults to `true` for unknown flags so new features don't hard-lock
 * until the admin explicitly disables them.
 */
export default function useFeatureFlags(userId) {
  const [flags, setFlags] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setFlags({});
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    const reload = async () => {
      const merged = await featureGatingService.getAllFeatureFlags(userId);
      if (!cancelled) {
        setFlags(merged);
        setLoading(false);
      }
    };

    reload();

    // Live updates: refresh when admin edits overrides or plan config.
    const unsubOverrides = onSnapshot(doc(db, 'userOverrides', userId), () => {
      featureGatingService.invalidateOverridesCache();
      reload();
    });
    const unsubPlans = onSnapshot(doc(db, 'adminConfig', 'subscriptionPlans'), () => {
      reload();
    });

    return () => {
      cancelled = true;
      unsubOverrides();
      unsubPlans();
    };
  }, [userId]);

  const isEnabled = (flagKey, fallback = true) => {
    if (typeof flags[flagKey] === 'boolean') return flags[flagKey];
    return fallback;
  };

  return { flags, isEnabled, loading };
}
